/**
 * 거래 일괄 입력(A-7) — Draft 행 → 저장용 입력값 변환
 *
 * @why 화면이 다루는 편집 중 값(문자열 금액, 빈 문자열 허용)과
 * 저장 계층이 요구하는 값(숫자 금액, null 허용)의 형태가 다르다.
 * 이 경계를 한 곳에 모아 두면 표현(테이블/카드)이 바뀌어도 저장 규칙이 흔들리지 않는다.
 */

import type { TransactionInput } from '../types/Transaction'
import type { BulkTransactionDraftRow } from '../types/BulkTransaction'
import type { BulkValidationSummary } from './bulkTransactionValidator'

/**
 * 검증을 통과한 행들만 저장용 입력값으로 변환한다.
 *
 * @param rows 화면의 Draft 행 (입력 순서 유지)
 * @param summary `validateAll` 결과. 파싱된 금액과 빈행/오류 판정을 여기서 가져온다
 * @returns 저장 대상 입력값 배열 (빈 행·오류 행 제외)
 */
export function toTransactionInputs(
  rows: BulkTransactionDraftRow[],
  summary: BulkValidationSummary
): TransactionInput[] {
  const inputs: TransactionInput[] = []

  for (const row of rows) {
    const validation = summary.byClientId[row.clientId]

    // 빈 행은 저장 대상이 아니고, 오류 행은 애초에 저장이 차단된다.
    // 매퍼에서도 한 번 더 걸러 불완전한 값이 payload에 섞이지 않게 한다.
    if (!validation || validation.isEmpty) continue
    if (validation.errors.length > 0) continue
    if (validation.amount === null) continue

    const isIncome = row.type === 'income'

    inputs.push({
      date: row.date,
      type: row.type,
      // 검증에서 이미 파싱한 값을 재사용한다(파서를 하나로 유지)
      amount: validation.amount,
      majorCategoryId: row.majorCategoryId,
      minorCategoryId: row.minorCategoryId || null,
      // 수입은 "입금"이라 결제수단 개념이 없다. 지출에서 전환되며 값이 남아 있어도
      // 화면에 칸이 없어 사용자가 지울 수 없으므로 여기서 빈 값으로 정규화한다.
      paymentMethodId: isIncome ? '' : row.paymentMethodId,
      spenderId: row.spenderId,
      memo: row.memo,
    })
  }

  return inputs
}
